/**
 * Warm every cache the Deal Dossier reads on mount, before the user opens it.
 *
 * Opening a deal used to start eleven cold requests at the moment of the
 * double-click, then a second wave once `/loans/{id}` had named the intake
 * record, then a third once the address had been composed — three to five
 * seconds of "—" in every slot while the shell sat there rendered. Nothing in
 * that sequence needed the click: the pipeline row already carries the loan
 * id, the intake id and the address.
 *
 * So the pipeline warms the dossier the moment a row is selected or hovered.
 * `prefetchQuery` deduplicates against in-flight and fresh queries, and every
 * key here is the same key the dossier reads (see `DOSSIER_QUERY_KEYS`), so
 * by the time the dossier mounts it renders from cache and the network is a
 * background refresh, not a gate.
 */

import type { QueryClient } from '@tanstack/react-query'
import type { Loan } from '@/types'
import { dealFileService } from '@/services/dealFile'
import { documentsService } from '@/services/documents'
import { loansService } from '@/services/loans'
import { mapsConfigService } from '@/services/mapsConfig'
import { ingestionService } from '@/services/ingestion'
import {
  dossierContextService,
  getDealAttribution,
  getDealCorrespondence,
} from '@/services/dossierContext'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { httpStatus } from '@/components/deal/dossier/httpStatus'

/** Keys the dossier reads. Kept in one place so the prefetch can never drift. */
export const DOSSIER_QUERY_KEYS = {
  notes: (id: string) => ['loan-notes', id] as const,
  timeline: (id: string) => ['loan', id, 'stage-timeline'] as const,
  documents: (id: string) => ['loan', id, 'documents'] as const,
  context: (id: string) => LOAN_QUERY_KEYS.dossierContext(id),
  correspondence: (id: string) => ['loan', id, 'correspondence'] as const,
  attribution: (id: string) => ['loan', id, 'attribution'] as const,
  ingestionDossier: (sid: string | null | undefined) => ['ingestion-dossier', sid] as const,
  ingestionRecord: (sid: string | null | undefined) => ['ingestion-record', sid] as const,
  mapsConfig: ['settings', 'maps-config'] as const,
  siteIntel: (id: string, address: string) => ['property-intel', id, address] as const,
  media: (id: string, address: string) => ['property-media', id, address] as const,
}

/**
 * How long a per-loan read stays fresh. Five minutes: a deal's notes,
 * lineage and timeline do not change under a reader's feet, and every write
 * path invalidates these keys explicitly (`invalidateLoanQueries`). The old
 * 30-second default meant re-opening the same deal a minute later refetched
 * everything.
 */
export const DOSSIER_STALE_MS = 5 * 60 * 1000

/** `/loans/{id}` 404s for illustrative pipeline-book rows: treat as no overlay. */
export async function readDealFileValues(loanId: string) {
  try {
    return await dealFileService.getValues(loanId)
  } catch (err) {
    if (httpStatus(err) === 404) return null
    throw err
  }
}

type Options = {
  /** Demo/illustrative rows are not on the server — skip the record reads. */
  demo?: boolean
}

/** Fire-and-forget. Never throws: a failed warm-up is a normal cold open. */
export function prefetchDossier(queryClient: QueryClient, loan: Loan, opts: Options = {}): void {
  const loanId = loan.id
  if (!loanId) return
  const stale = DOSSIER_STALE_MS
  const jobs: Promise<unknown>[] = [
    queryClient.prefetchQuery({
      queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
      queryFn: () => dealFileService.getCatalogue(),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: DOSSIER_QUERY_KEYS.mapsConfig,
      queryFn: () => mapsConfigService.get(),
      staleTime: stale,
    }),
  ]
  if (!opts.demo) {
    jobs.push(
      queryClient.prefetchQuery({
        queryKey: LOAN_QUERY_KEYS.dealFile(loanId),
        queryFn: () => readDealFileValues(loanId),
        staleTime: stale,
      }),
      queryClient.prefetchQuery({
        queryKey: DOSSIER_QUERY_KEYS.context(loanId),
        queryFn: () => dossierContextService.getContext(loanId),
        staleTime: stale,
      }),
    )
  }
  jobs.push(
    queryClient.prefetchQuery({
      queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId),
      queryFn: () => loansService.getFieldLineage(loanId),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: DOSSIER_QUERY_KEYS.notes(loanId),
      queryFn: () => loansService.listNotes(loanId),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: LOAN_QUERY_KEYS.activity(loanId),
      queryFn: () => loansService.getActivity(loanId),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: DOSSIER_QUERY_KEYS.timeline(loanId),
      queryFn: () => loansService.getStageTimeline(loanId),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: DOSSIER_QUERY_KEYS.documents(loanId),
      queryFn: () => documentsService.listForLoan(loanId),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: DOSSIER_QUERY_KEYS.correspondence(loanId),
      queryFn: () => getDealCorrespondence(loanId),
      staleTime: stale,
    }),
    queryClient.prefetchQuery({
      queryKey: DOSSIER_QUERY_KEYS.attribution(loanId),
      queryFn: () => getDealAttribution(loanId),
      staleTime: stale,
    }),
  )
  const sid = loan.sourceIngestionId
  if (sid) {
    jobs.push(
      queryClient.prefetchQuery({
        queryKey: DOSSIER_QUERY_KEYS.ingestionDossier(sid),
        queryFn: () => ingestionService.getDossier(sid),
        staleTime: stale,
      }),
      queryClient.prefetchQuery({
        queryKey: DOSSIER_QUERY_KEYS.ingestionRecord(sid),
        queryFn: () => ingestionService.getById(sid),
        staleTime: stale,
      }),
    )
  }
  void Promise.allSettled(jobs)
}

const HOVER_DELAY_MS = 120

/**
 * A hover-driven prefetch that does not fire on every row the pointer
 * crosses on its way somewhere else. Returns the enter/leave handlers.
 */
export function makeHoverPrefetch(run: (loan: Loan) => void) {
  let timer: ReturnType<typeof setTimeout> | null = null
  return {
    enter(loan: Loan) {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        run(loan)
      }, HOVER_DELAY_MS)
    },
    leave() {
      if (timer) clearTimeout(timer)
      timer = null
    },
  }
}
